import {Dialog, useMediaQuery, useTheme} from "@mui/material";
import {Folio, FolioStatus} from "crqlar-react-common/dist/models/billing/folio.model";
import {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {ReservationGroup} from "crqlar-react-common/dist/models/reservation/reservationGroup.model";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {FolioService} from "crqlar-react-common/dist/services/billing/folio.service";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import {useSnackbar} from "notistack";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQueryClient} from "react-query";
import {FOLIO_DIGITAL_SIGNINGS_KEY} from "../../../../digitalSigning/digitalSigningQueries";
import FolioEditorView from "./FolioEditorView";
import {useFolioFormat} from "./folioFormat";
import FolioHeader, {FOLIO_HEADER_TITLE_ID, FolioHeaderProps} from "./FolioHeader";
import FolioListView from "./FolioListView";
import {invalidateFolioLists, prefetchFolioItems} from "./folioQueryKeys";
import {FOLIO_DIALOG_MAX_WIDTH, FOLIO_DIALOG_MIN_HEIGHT} from "./folioStyles";
import {useFolioLineDefaults} from "./useFolioLineDefaults";
import {sortFoliosByDate} from "./folioList.util";
import {folioListKey, FolioListScope, useFolioList} from "./useFolioList";
import {useFolioSession} from "./useFolioSession";

export type FolioDialogView = 'folio' | 'list';

// As tall as its content, like the deprecated Folios dialog, from its least height on: header and
// footer stay, the middle scrolls once the content is taller than the screen allows
const PAPER_SX = {
    maxWidth: FOLIO_DIALOG_MAX_WIDTH,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    // Nothing below 12px - also in the shared parts (payment forms, digital signing)
    '& .MuiTypography-caption': {fontSize: 12, lineHeight: '16px'},
};
// Never above the dialog's own maximum, on a short window. Full screen it is the screen's height
const MIN_HEIGHT_SX = {minHeight: `min(${FOLIO_DIALOG_MIN_HEIGHT}px, calc(100% - 64px))`};

interface Props {
    open: boolean
    onClose: () => void
    reservation?: Reservation
    /** The reservation's service. */
    service?: Service
    /**
     * The package the reservation is a member of (a group of two or more): its one folio is listed
     * beside the reservation's own, and billed to one of the package's guests.
     */
    packageGroup?: ReservationGroup
    /** The folio the folio view shows. */
    folioId?: string
    /** The folio is still being prepared (the Charge flow): the folio view shows its frame meanwhile. */
    preparing?: boolean
    initialView: FolioDialogView
    /** A folio changed: finalized, confirmed, charged, paid … */
    onChanged?: (folio: Folio) => void
}

/**
 * The reservation's folios in one dialog, a child of the reservation dialog - as wide as the deprecated
 * Folios dialog, as tall as what it shows (at least as tall as a folio with a line or two): a folio
 * (its lines, the signing, the payment) or the list of all of them - a package's member's
 * with the package's one folio -, which opens and creates folios. A package's folio has the member it
 * was opened from as its reservation: the sector, the signing's default tip recipient. The folio's draft is saved whenever the folio is left - back to the list, close -
 * and before it is refreshed or printed.
 */
const FolioDialog = ({open, onClose, reservation, service, packageGroup, folioId, preparing, initialView, onChanged}: Props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const queryClient = useQueryClient();
    const organization = useOrganization();
    const {enqueueSnackbar} = useSnackbar();
    const sector = useSector(reservation?.sectorId);
    const [view, setView] = useState<FolioDialogView>(initialView);
    const [activeFolioId, setActiveFolioId] = useState(folioId);
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        if (!open) return;
        setView(initialView);
        setActiveFolioId(folioId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    // The Charge flow hands its folio over once it's prepared
    useEffect(() => {
        if (folioId) setActiveFolioId(folioId);
    }, [folioId]);
    // No folio to show (its preparation failed): the reservation's folios instead
    useEffect(() => {
        if (open && view === 'folio' && !activeFolioId && !folioId && !preparing) setView('list');
    }, [open, view, activeFolioId, folioId, preparing]);

    const session = useFolioSession({open, folioId: activeFolioId, reservation, service, onChanged});
    useFolioLineDefaults({session, reservation, service, folioId});
    const {folio, draft} = session;

    const reservationId = reservation?.id ?? folio?.reservationId;
    const scope: FolioListScope = {
        reservationId,
        // A package's member lists the package's folio too; a folio without a reservation its group's -
        // any other group member works like a single reservation
        reservationGroupId: packageGroup?.id ?? (reservationId ? undefined : folio?.reservationGroupId ?? reservation?.reservationGroupId),
    };
    const listQuery = useFolioList(scope, open);
    // The reservation's folios are few: their lines are read ahead, so a folio opened from the list
    // shows them at once
    useEffect(() => {
        if (open && listQuery.data) prefetchFolioItems(queryClient, listQuery.data);
    }, [open, listQuery.data, queryClient]);
    const currency = folio?.currency || organization?.currencyCode || 'EUR';
    const format = useFolioFormat(currency);
    const busy = draft.saving || creating;
    // Without a reservation or group there is nothing to list - never the organization's folios
    const listed = !!scope.reservationId || !!scope.reservationGroupId;
    const canCreate = !session.readOnlyUser && listed;

    // Leaving the folio saves its draft; a failed save stays here, with the error above the lines
    const leaveFolio = () => draft.persist();

    const openFolio = async (id: string) => {
        if (id !== activeFolioId) {
            if (!(await leaveFolio())) return;
            setActiveFolioId(id);
            invalidateFolioLists(queryClient);
        }
        setView('folio');
    };

    const showList = async () => {
        if (await leaveFolio()) setView('list');
    };

    const createFolio = async () => {
        if (busy || !(await leaveFolio())) return;
        setCreating(true);
        try {
            const created = await FolioService.create({
                name: [sector?.name, t('Res.')].filter(Boolean).join(' '),
                status: FolioStatus.DRAFT,
                profileId: reservation?.primaryProfileId || reservation?.profileIds?.[0] || folio?.profileId,
                reservationId: scope.reservationId,
                reservationGroupId: scope.reservationId ? undefined : scope.reservationGroupId,
                // The organization's, like the charge flow's folio (the backend defaults to it too)
                currency: organization?.currencyCode,
            });
            invalidateFolioLists(queryClient);
            if (created?.id) {
                // In the cached list at once, so its header shows while the lists read again
                queryClient.setQueryData<Folio[] | undefined>(folioListKey(scope), current => current && sortFoliosByDate([
                    ...current.filter(listed => listed.id !== created.id),
                    created,
                ]));
                setActiveFolioId(created.id);
                setView('folio');
            }
        } catch (error) {
            console.error('Error creating a folio:', error);
            enqueueSnackbar(t('The folio could not be created.'), {variant: 'error'});
        }
        setCreating(false);
    };

    const close = async () => {
        if (!busy && (await leaveFolio())) onClose();
    };

    // The printout shows what is saved: the tab opens within the click (popup blockers), the printout
    // loads once the draft is saved
    const print = async () => {
        if (!folio?.id) return;
        const url = `${window.location.origin}/print/folio/${folio.id}`;
        if (!draft.isDirty) {
            window.open(url, '_blank');
            return;
        }
        const printWindow = window.open('', '_blank');
        if (await draft.persist()) {
            if (printWindow) printWindow.location.href = url;
            else window.open(url, '_blank');
        } else {
            printWindow?.close();
        }
    };

    // The list view's Refresh reads the folios again - the table follows each new read of them - and
    // their signings: one confirmed on another device meanwhile gets its icon
    const refreshList = async () => {
        const [list] = await Promise.all([
            listQuery.refetch(),
            queryClient.invalidateQueries(FOLIO_DIGITAL_SIGNINGS_KEY),
        ]);
        // A read that failed doesn't reject: Refresh shows its error mark instead of a check
        return !list.isError;
    };

    const header: FolioHeaderProps = {
        folio,
        folios: listQuery.data,
        busy,
        onShowAll: showList,
        onClose: close,
    };

    return (
        <Dialog
            open={open}
            onClose={close}
            maxWidth={false}
            fullWidth
            fullScreen={fullScreen}
            aria-labelledby={FOLIO_HEADER_TITLE_ID}
            PaperProps={{sx: fullScreen ? PAPER_SX : {...PAPER_SX, ...MIN_HEIGHT_SX}}}
        >
            {/* The folio view renders the header itself: it shows the signing the view follows.
                Keyed by the folio, so nothing of one folio stays with another */}
            {view === 'folio' ? (
                <FolioEditorView
                    key={activeFolioId}
                    open={open}
                    header={header}
                    session={session}
                    reservation={reservation}
                    service={service}
                    packageGroup={packageGroup}
                    currency={currency}
                    format={format}
                    onPrint={print}
                />
            ) : (
                <>
                    <FolioHeader {...header} view={'list'} onRefresh={listed ? refreshList : undefined}/>
                    <FolioListView
                        scope={scope}
                        format={format}
                        busy={busy}
                        canCreate={canCreate}
                        onOpen={openFolio}
                        onCreate={createFolio}
                        onClose={close}
                    />
                </>
            )}
        </Dialog>
    );
};

export default FolioDialog;
